/**
 * Main PDF player orchestrator composable
 * Combines document loading, navigation, zoom, and search functionality
 */

import { ref, shallowRef, computed, watch, markRaw, type Ref, type ComputedRef, type ShallowRef } from 'vue'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import type {
  PDFPlayerConfig,
  PDFPlayerState,
  PDFPlayerCallbacks,
  PDFOutlineItem,
  PDFSearchResult,
  PDFSearchSuggestion,
  PDFSearchMode,
  PDFSearchQuerySource,
  PDFViewMode,
  PDFZoomMode,
  PDFZoom,
} from '../types'
import { usePDFDocument, type UsePDFDocumentReturn } from './usePDFDocument'
import { usePDFNavigation, type UsePDFNavigationReturn } from './usePDFNavigation'
import { usePDFSearch, type UsePDFSearchReturn } from './usePDFSearch'
import {
  resolveZoom,
  clampZoom,
  incrementZoom,
  decrementZoom,
  calculateTwoPageZoom,
  ZOOM_PRESETS,
  type ContainerDimensions,
  type PageDimensions,
} from '../utils/zoom-utils'

export interface UsePDFPlayerOptions {
  config: PDFPlayerConfig
  callbacks?: PDFPlayerCallbacks
}

export interface UsePDFPlayerReturn {
  // Document state
  document: ShallowRef<PDFDocumentProxy | null>
  isDocumentLoading: Ref<boolean>
  isDocumentLoaded: ComputedRef<boolean>
  documentError: Ref<Error | null>
  totalPages: Ref<number>
  outline: Ref<PDFOutlineItem[]>

  // Current page
  currentPage: Ref<number>
  currentPageProxy: ShallowRef<PDFPageProxy | null>
  getPage: (pageNumber: number) => Promise<PDFPageProxy>

  // Navigation
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  goToPage: (page: number) => void
  nextPage: () => void
  prevPage: () => void
  firstPage: () => void
  lastPage: () => void

  // View mode
  viewMode: Ref<PDFViewMode>
  setViewMode: (mode: PDFViewMode) => void

  // Zoom
  zoom: Ref<number>
  zoomMode: Ref<PDFZoomMode | null>
  setZoom: (zoom: PDFZoom) => void
  zoomIn: () => void
  zoomOut: () => void
  zoomPresets: number[]
  updateContainerSize: (dimensions: ContainerDimensions) => void

  // Search
  searchQuery: Ref<string>
  searchResults: Ref<PDFSearchResult[]>
  currentSearchIndex: Ref<number>
  isSearching: Ref<boolean>
  searchSuggestions: ComputedRef<PDFSearchSuggestion[]>
  navigableSearchResults: ComputedRef<PDFSearchResult[]>
  activeHighlightMatchIndex: ComputedRef<number>
  searchMode: Ref<PDFSearchMode>
  querySource: Ref<PDFSearchQuerySource>
  search: (query: string) => Promise<void>
  clearSearch: () => void
  nextSearchResult: () => void
  prevSearchResult: () => void
  selectViewAll: () => void
  selectWord: (word: string) => void

  // Rotation
  rotation: Ref<number>
  rotate: () => void

  // Fullscreen
  isFullscreen: Ref<boolean>
  toggleFullscreen: () => void

  // Actions
  print: () => void
  download: () => void

  // Lifecycle
  loadDocument: (url: string) => Promise<void>
  destroy: () => void

  // State (for debugging/external access)
  state: ComputedRef<PDFPlayerState>
}

/**
 * Main PDF player orchestrator
 */
export function usePDFPlayer(options: UsePDFPlayerOptions): UsePDFPlayerReturn {
  const { config, callbacks } = options

  // Container dimensions for zoom calculations
  const containerDimensions = ref<ContainerDimensions>({ width: 800, height: 600 })
  const pageDimensions = ref<PageDimensions>({ width: 612, height: 792 }) // Default letter size

  // Zoom state
  const zoom = ref(100)
  const zoomMode = ref<PDFZoomMode | null>(config.zoom ? (typeof config.zoom === 'string' ? config.zoom : null) : 'auto')

  // Document URL for printing
  const documentUrl = ref<string | null>(null)

  // Rotation state
  const rotation = ref(0)

  // Fullscreen state
  const isFullscreen = ref(false)

  // Current page proxy for rendering (use shallowRef to avoid Vue reactivity on pdf.js objects)
  const currentPageProxy = shallowRef<PDFPageProxy | null>(null)

  // Document composable
  const documentComposable: UsePDFDocumentReturn = usePDFDocument({
    onLoad: (totalPages, outline) => {
      callbacks?.onDocumentLoad?.(totalPages, outline)
    },
    onError: (error) => {
      callbacks?.onError?.({
        code: 'PLAYBACK_ERROR',
        message: error.message,
        originalError: error,
        recoverable: false,
      })
    },
  })

  // Navigation composable
  const navigationComposable: UsePDFNavigationReturn = usePDFNavigation({
    totalPages: documentComposable.totalPages,
    initialPage: config.startPage || 1,
    initialViewMode: config.viewMode || 'continuous',
    onPageChange: (page) => {
      callbacks?.onPageChange?.(page)
      loadCurrentPage()
    },
    onViewModeChange: (mode) => {
      callbacks?.onViewModeChange?.(mode)
    },
  })

  // Search composable
  const searchComposable: UsePDFSearchReturn = usePDFSearch({
    document: documentComposable.document,
    getTextContent: (pageNumber) => documentComposable.getTextContent(pageNumber),
    onSearchStart: (query) => {
      callbacks?.onSearchStart?.(query)
    },
    onSearchResults: (results) => {
      callbacks?.onSearchResults?.(results)
    },
    onResultSelect: (result, index) => {
      // Navigate to result page
      navigationComposable.goToPage(result.pageNumber)
      callbacks?.onSearchResultSelect?.(result, index)
    },
  })

  /**
   * Loads the current page proxy for rendering
   */
  async function loadCurrentPage(): Promise<void> {
    if (!documentComposable.isLoaded.value) return

    try {
      const page = await documentComposable.getPage(navigationComposable.currentPage.value)
      currentPageProxy.value = markRaw(page)

      // Update page dimensions for zoom calculations (rotation swaps w/h at 90/270)
      const viewport = page.getViewport({ scale: 1, rotation: rotation.value })
      pageDimensions.value = {
        width: viewport.width,
        height: viewport.height,
      }

      // Recalculate zoom if using a zoom mode
      if (zoomMode.value) {
        updateZoomFromMode()
      }
    } catch {
      // Page load errors are handled by the document composable
    }
  }

  /**
   * Updates zoom based on current zoom mode
   */
  function updateZoomFromMode(): void {
    if (zoomMode.value) {
      zoom.value = resolveZoom(
        zoomMode.value,
        containerDimensions.value,
        pageDimensions.value,
      )
    }
  }

  /**
   * Sets the zoom level
   */
  function setZoom(newZoom: PDFZoom): void {
    if (typeof newZoom === 'number') {
      zoom.value = clampZoom(newZoom)
      zoomMode.value = null
    } else {
      zoomMode.value = newZoom
      zoom.value = resolveZoom(
        newZoom,
        containerDimensions.value,
        pageDimensions.value,
      )
    }
    callbacks?.onZoomChange?.(zoom.value, zoomMode.value)
  }

  /**
   * Zooms in by one step
   */
  function zoomIn(): void {
    zoom.value = incrementZoom(zoom.value)
    zoomMode.value = null
    callbacks?.onZoomChange?.(zoom.value, null)
  }

  /**
   * Zooms out by one step
   */
  function zoomOut(): void {
    zoom.value = decrementZoom(zoom.value)
    zoomMode.value = null
    callbacks?.onZoomChange?.(zoom.value, null)
  }

  /**
   * Updates container size (call when container resizes)
   */
  function updateContainerSize(dimensions: ContainerDimensions): void {
    containerDimensions.value = dimensions
    if (navigationComposable.viewMode.value === 'two-page') {
      zoom.value = calculateTwoPageZoom(dimensions, pageDimensions.value)
    } else if (zoomMode.value) {
      updateZoomFromMode()
    }
  }

  /**
   * Toggles fullscreen mode
   */
  function toggleFullscreen(): void {
    isFullscreen.value = !isFullscreen.value
    callbacks?.onFullscreenChange?.(isFullscreen.value)
  }

  /**
   * Rotates the document 90 degrees clockwise
   */
  function rotate(): void {
    rotation.value = (rotation.value + 90) % 360
    // Recalculate page dimensions with new rotation
    loadCurrentPage()
    callbacks?.onRotationChange?.(rotation.value)
  }

  /**
   * Opens the browser print dialog directly
   */
  function print(): void {
    callbacks?.onPrint?.()
    window.print()
  }

  /**
   * Downloads the document
   */
  function download(): void {
    callbacks?.onDownload?.()
  }

  /**
   * Loads a PDF document from URL
   */
  async function loadDocument(url: string): Promise<void> {
    documentUrl.value = url
    await documentComposable.loadDocument(url)

    // Load initial page
    if (documentComposable.isLoaded.value) {
      await loadCurrentPage()

      // Set initial zoom
      if (config.zoom) {
        setZoom(config.zoom)
      } else {
        setZoom('auto')
      }

      // Preload text content for search performance
      if (config.enableSearch !== false) {
        documentComposable.preloadTextContent()
      }
    }
  }

  /**
   * Cleans up resources
   */
  function destroy(): void {
    documentComposable.destroy()
    searchComposable.clearSearch()
    currentPageProxy.value = null
  }

  // Computed state for external access
  const state = computed<PDFPlayerState>(() => ({
    isDocumentLoading: documentComposable.isLoading.value,
    isDocumentLoaded: documentComposable.isLoaded.value,
    error: documentComposable.error.value
      ? {
          code: 'PLAYBACK_ERROR' as const,
          message: documentComposable.error.value.message,
          originalError: documentComposable.error.value,
          recoverable: false,
        }
      : undefined,
    currentPage: navigationComposable.currentPage.value,
    totalPages: documentComposable.totalPages.value,
    outline: documentComposable.outline.value,
    zoom: zoom.value,
    zoomMode: zoomMode.value,
    viewMode: navigationComposable.viewMode.value,
    rotation: rotation.value,
    isFullscreen: isFullscreen.value,
    searchQuery: searchComposable.searchQuery.value,
    searchResults: searchComposable.searchResults.value,
    currentSearchResultIndex: searchComposable.activeHighlightMatchIndex.value,
    playbackInfo: {
      isLoading: documentComposable.isLoading.value,
      isLoaded: documentComposable.isLoaded.value,
    },
  }))

  // Saved zoom state before entering two-page mode
  let savedZoomBeforeTwoPage: { zoom: number; mode: PDFZoomMode | null } | null = null

  // Watch view mode changes for zoom adjustment
  watch(
    () => navigationComposable.viewMode.value,
    (newMode, oldMode) => {
      if (newMode === 'two-page') {
        savedZoomBeforeTwoPage = { zoom: zoom.value, mode: zoomMode.value }
        zoom.value = calculateTwoPageZoom(
          containerDimensions.value,
          pageDimensions.value,
        )
        zoomMode.value = null
        callbacks?.onZoomChange?.(zoom.value, null)
      } else if (oldMode === 'two-page' && savedZoomBeforeTwoPage) {
        zoom.value = savedZoomBeforeTwoPage.zoom
        zoomMode.value = savedZoomBeforeTwoPage.mode
        if (zoomMode.value) updateZoomFromMode()
        callbacks?.onZoomChange?.(zoom.value, zoomMode.value)
        savedZoomBeforeTwoPage = null
      } else if (zoomMode.value) {
        updateZoomFromMode()
      }
    },
  )

  return {
    // Document state
    document: documentComposable.document,
    isDocumentLoading: documentComposable.isLoading,
    isDocumentLoaded: documentComposable.isLoaded,
    documentError: documentComposable.error,
    totalPages: documentComposable.totalPages,
    outline: documentComposable.outline,

    // Current page
    currentPage: navigationComposable.currentPage,
    currentPageProxy,
    getPage: documentComposable.getPage,

    // Navigation
    canGoNext: navigationComposable.canGoNext,
    canGoPrev: navigationComposable.canGoPrev,
    goToPage: navigationComposable.goToPage,
    nextPage: navigationComposable.nextPage,
    prevPage: navigationComposable.prevPage,
    firstPage: navigationComposable.firstPage,
    lastPage: navigationComposable.lastPage,

    // View mode
    viewMode: navigationComposable.viewMode,
    setViewMode: navigationComposable.setViewMode,

    // Zoom
    zoom,
    zoomMode,
    setZoom,
    zoomIn,
    zoomOut,
    zoomPresets: ZOOM_PRESETS,
    updateContainerSize,

    // Search
    searchQuery: searchComposable.searchQuery,
    searchResults: searchComposable.searchResults,
    currentSearchIndex: searchComposable.currentIndex,
    isSearching: searchComposable.isSearching,
    searchSuggestions: searchComposable.suggestions,
    navigableSearchResults: searchComposable.navigableResults,
    activeHighlightMatchIndex: searchComposable.activeHighlightMatchIndex,
    searchMode: searchComposable.searchMode,
    querySource: searchComposable.querySource,
    search: searchComposable.search,
    clearSearch: searchComposable.clearSearch,
    nextSearchResult: searchComposable.nextResult,
    prevSearchResult: searchComposable.prevResult,
    selectViewAll: searchComposable.selectViewAll,
    selectWord: searchComposable.selectWord,

    // Rotation
    rotation,
    rotate,

    // Fullscreen
    isFullscreen,
    toggleFullscreen,

    // Actions
    print,
    download,

    // Lifecycle
    loadDocument,
    destroy,

    // State
    state,
  }
}
